/**
 * Derived PCO status (funding buckets, findings #13 + #8).
 *
 * The PCO status pill is no longer read from `pcos.status` for the "active"
 * family — it's derived read-time from the money picture. Only the two
 * terminal lifecycle values (`paid` / `void`) still come from the stored
 * status; everything else is computed from approved/pending/proposed so the
 * pill can never drift from the numbers beside it.
 *
 * Single source of truth for PcoLogPage, PcoDetailPage, and PcoDrawer — each
 * consumer maps `kind` to its own visual (StatusPill kind or status-chip
 * class) via the small helpers below.
 *
 *   void   → "Void"                (stored lifecycle)
 *   paid   → "Paid"                (stored lifecycle)
 *   approved>0 && pending===0      → "Funded"
 *   approved>0 && pending>0        → "Partially funded"
 *   approved===0 && (pending>0 || proposed!=null) → "Proposed"
 *   else                           → "Open"
 *
 * "pending" above is FUNDING-side pending — the proposal gap plus pending
 * buckets — which is `funding_pending_cents`, not `pending_cents`. As of
 * 2026-07-28 `pending_cents` also carries billing that has been submitted
 * and is awaiting the GC's approval; reading that here would demote a fully
 * funded PCO to "Partially funded" the moment it billed a week, which is the
 * wrong question entirely (the chip is about the GC executing the FUNDING).
 * The fallback to `pending_cents` is deliberate fail-open: Pages can be live
 * with a Worker that hasn't shipped the new field yet, and the pre-2026-07-28
 * behavior is the right thing to degrade to.
 *
 * The funded family says FUNDED, not APPROVED (client, PCO 062, 2026-07-28).
 * A bare "Approved" chip reads as an answer to whichever "approved" question
 * the neighboring money columns raise — the GC executing the funding (the
 * chip's actual subject) or the GC accepting billings. Two different
 * questions, one word, so the chip takes the unambiguous one. Only
 * the display label moved: the internal kind stays `approved`, the stored
 * `pcos.status` enum still uses `'approved'`, and the chip keeps its class and
 * color.
 */
import type { PcoStatus } from '@coffman-tm/shared';
import type { StatusPillKind } from '../components/design';

export type DerivedPcoKind =
  | 'void'
  | 'paid'
  | 'approved'
  | 'partial'
  | 'proposed'
  | 'open';

/** The money + lifecycle inputs the derivation reads. */
export interface DerivedPcoInput {
  status: PcoStatus;
  approved_total_cents: number;
  /**
   * Total pending — proposal gap + pending buckets + billing submitted but
   * not yet approved to bill. Only read when `funding_pending_cents` is
   * absent (see below).
   */
  pending_cents: number;
  /**
   * FUNDING-side pending only. What the chip actually wants. Optional: an
   * API that predates the split doesn't send it, and the fallback to
   * `pending_cents` restores the old behavior rather than blanking the chip.
   */
  funding_pending_cents?: number;
  amount_proposed_cents: number | null;
}

export interface DerivedPcoStatus {
  kind: DerivedPcoKind;
  label: string;
}

export function derivePcoStatus(p: DerivedPcoInput): DerivedPcoStatus {
  // PCO-level lifecycle wins — paid/void live in pcos.status, not the buckets.
  if (p.status === 'void') return { kind: 'void', label: 'Void' };
  if (p.status === 'paid') return { kind: 'paid', label: 'Paid' };

  const approved = p.approved_total_cents;
  // Funding-side pending when the API sends it; total pending otherwise.
  const pending = p.funding_pending_cents ?? p.pending_cents;
  if (approved > 0 && pending === 0) return { kind: 'approved', label: 'Funded' };
  if (approved > 0 && pending > 0) return { kind: 'partial', label: 'Partially funded' };
  if (approved === 0 && (pending > 0 || p.amount_proposed_cents != null)) {
    return { kind: 'proposed', label: 'Proposed' };
  }
  return { kind: 'open', label: 'Open' };
}

/** `<StatusPill kind>` variant for a derived kind (PcoLog / PcoDrawer). */
export function derivedPillKind(kind: DerivedPcoKind): StatusPillKind {
  switch (kind) {
    case 'void':
      return 'void';
    case 'paid':
    case 'approved':
      return 'ready';
    case 'partial':
    case 'proposed':
      return 'pending';
    case 'open':
      return 'neutral';
  }
}

/**
 * `.status-chip s-*` class suffix for a derived kind (PcoDetailPage header).
 * Reuses the existing chip palette — green for approved/paid, warning for the
 * pending family, gray for void, accent for open.
 */
export function derivedChipClass(kind: DerivedPcoKind): string {
  switch (kind) {
    case 'void':
      return 's-void';
    case 'paid':
      return 's-paid';
    case 'approved':
      return 's-approved';
    case 'partial':
    case 'proposed':
      return 's-pending';
    case 'open':
      return 's-open';
  }
}
